/**
 * Model and words (spec §2 H) and cost and limits (§2 K).
 *
 * The workspace's words sit under the agent's built-in contract — they can
 * specialize wording, never replace the contract or widen what the agent may
 * read or write. The words editor itself is shared with the agent creator
 * (AgentWordsEditor). Temperature and length can be lowered below the platform
 * bound, never raised past it. Every model call counts against the monthly
 * cap under this agent's meter key.
 */

import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Select } from '../../ds';
import { AGENT_LANGUAGES, AGENT_MODELS, LIMIT_BOUNDS, type AgentLimits } from '../../../lib/ai/agentDefinitions';
import { getAgentAiUsage } from '../../../lib/agentRuntimeService';
import { resolveModelParams } from '../../../../supabase/functions/_shared/estimateValueCore.ts';
import { SwitchGroup, type SectionProps } from './shared';
import { stored } from './resolve';
import { AgentWordsEditor } from './AgentWordsEditor';

export function ModelSection({ def, config, isSaving, canEdit, save }: SectionProps) {
  const params = resolveModelParams(def.modelDefaults, config?.modelParams);
  const putParams = (patch: Partial<typeof params>, ok?: string) => save({ modelParams: { ...params, ...patch } }, ok);
  const [prompt, setPrompt] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setPrompt(config?.systemPrompt ?? ''); setDirty(false); }, [config?.systemPrompt]);

  return (
    <SwitchGroup title="Model and words" lead="Your words specialize the agent; they cannot replace its contract or widen what it may read or write.">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`model-${def.agentType}`}>Model</label>
          <Select id={`model-${def.agentType}`} value={config?.model ?? def.defaultModel} disabled={isSaving || !canEdit}
            onChange={(e) => void save({ model: e.target.value }, 'Model saved')}>
            {AGENT_MODELS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            {config?.model && !AGENT_MODELS.some((m) => m.id === config.model) && <option value={config.model}>{config.model}</option>}
          </Select>
          <p className="text-[11px] text-[var(--text-ter)] mt-1">Automatic runs on the server use the platform's own key. A model it has no key for runs on GPT-6.1 Sol, or on Claude Sonnet 5.5 when only a Claude key is set.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`lang-${def.agentType}`}>Language of its rationale</label>
          <Select id={`lang-${def.agentType}`} value={params.language ?? ''} disabled={isSaving || !canEdit}
            onChange={(e) => void putParams({ language: e.target.value || null })}>
            <option value="">Platform default (English)</option>
            {AGENT_LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Temperature: {params.temperature.toFixed(2)} (at most {def.modelDefaults.temperature})</label>
          <input type="range" min={0} max={def.modelDefaults.temperature} step={0.05} value={params.temperature} disabled={isSaving || !canEdit}
            onChange={(e) => void putParams({ temperature: Number(e.target.value) })} className="w-full accent-[var(--slate)]" aria-label="Temperature" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Longest answer: {params.maxTokens} tokens (at most {def.modelDefaults.maxTokens})</label>
          <input type="range" min={100} max={def.modelDefaults.maxTokens} step={50} value={params.maxTokens} disabled={isSaving || !canEdit}
            onChange={(e) => void putParams({ maxTokens: Number(e.target.value) })} className="w-full accent-[var(--slate)]" aria-label="Longest answer" />
        </div>
      </div>

      <div className="border-t border-[var(--rule)] pt-3">
        {/* The same editor the agent creator mounts; the words are saved here, on this page. */}
        <AgentWordsEditor
          id={`words-${def.agentType}`}
          value={prompt}
          readOnly={!canEdit}
          onChange={(next) => { setPrompt(next); setDirty(true); }}
          contract={def.defaultSystemPrompt}
          action={canEdit ? (
            <div className="flex items-center gap-2">
              {dirty && (
                <Button variant="primary" size="sm" disabled={isSaving} onClick={() => void save({ systemPrompt: prompt.trim() || null }, 'Words saved').then(() => setDirty(false))}>
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                </Button>
              )}
              {prompt.trim() && <Button variant="ghost" size="sm" disabled={isSaving} onClick={() => { setPrompt(''); void save({ systemPrompt: null }, 'Words removed'); }}>Remove</Button>}
            </div>
          ) : undefined}
        />
      </div>
    </SwitchGroup>
  );
}

export function LimitsSection({ def, config, workspaceId, isSaving, canEdit, save }: SectionProps) {
  // An agent that uses no model (Review Due) has nothing to meter.
  const usesNoModel = def.evidenceModel === 'deterministic_only';
  const l = stored<AgentLimits>(def.limits, config?.limits);
  const [usage, setUsage] = useState<{ used: number; limit: number; feature: string } | null>(null);
  const [draft, setDraft] = useState({ monthlyCap: String(l.monthlyCap), perRunObjectCap: String(l.perRunObjectCap), batchSize: String(l.batchSize), timeoutSec: String(Math.round(l.timeoutMs / 1000)) });
  useEffect(() => {
    setDraft({ monthlyCap: String(l.monthlyCap), perRunObjectCap: String(l.perRunObjectCap), batchSize: String(l.batchSize), timeoutSec: String(Math.round(l.timeoutMs / 1000)) });
  }, [l.monthlyCap, l.perRunObjectCap, l.batchSize, l.timeoutMs]);
  useEffect(() => { void getAgentAiUsage(workspaceId, def.agentType).then(setUsage); }, [workspaceId, def.agentType, config?.limits]);

  const clamp = (v: string, b: { min: number; max: number }) => Math.max(b.min, Math.min(b.max, Math.round(Number(v) || b.min)));
  const commit = (key: keyof typeof draft) => {
    const next: Partial<AgentLimits> =
      key === 'monthlyCap' ? { monthlyCap: clamp(draft.monthlyCap, LIMIT_BOUNDS.monthlyCap) }
      : key === 'perRunObjectCap' ? { perRunObjectCap: clamp(draft.perRunObjectCap, LIMIT_BOUNDS.perRunObjectCap) }
      : key === 'batchSize' ? { batchSize: clamp(draft.batchSize, LIMIT_BOUNDS.batchSize) }
      : { timeoutMs: clamp(String(Number(draft.timeoutSec) * 1000), LIMIT_BOUNDS.timeoutMs) };
    const changed = Object.entries(next).some(([k, v]) => (l as unknown as Record<string, unknown>)[k] !== v);
    if (changed) void save({ limits: { ...l, ...next } }, 'Limit saved');
  };
  const field = (key: keyof typeof draft, label: string, help: string) => (
    <div>
      <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`${key}-${def.agentType}`}>{label}</label>
      <input id={`${key}-${def.agentType}`} type="number" value={draft[key]} readOnly={!canEdit}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} onBlur={() => commit(key)}
        className="w-full px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--surface)]" />
      <p className="text-[11px] text-[var(--text-ter)] mt-0.5">{help}</p>
    </div>
  );

  return (
    <SwitchGroup title="Cost and limits" lead={usesNoModel ? 'It uses no model, so nothing is metered.' : `Model calls are counted under "${usage?.feature ?? `agent:${l.meterFeature}`}".${usage ? ` This month: ${usage.used} of ${usage.limit}.` : ''}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        {!usesNoModel && field('monthlyCap', 'Model calls a month', 'When it is reached, runs record the reason and stop calling the model until next month.')}
        {field('perRunObjectCap', 'Items per run', `At most ${LIMIT_BOUNDS.perRunObjectCap.max}.`)}
        {field('batchSize', 'Items per batch', `At most ${LIMIT_BOUNDS.batchSize.max}.`)}
        {field('timeoutSec', 'Seconds before a call gives up', `Between ${LIMIT_BOUNDS.timeoutMs.min / 1000} and ${LIMIT_BOUNDS.timeoutMs.max / 1000}.`)}
      </div>
      <div className="border-t border-[var(--rule)] pt-3 max-w-sm">
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`posture-${def.agentType}`}>When something fails</label>
        <Select id={`posture-${def.agentType}`} value={l.failurePosture} disabled={isSaving || !canEdit}
          onChange={(e) => void save({ limits: { ...l, failurePosture: e.target.value as AgentLimits['failurePosture'] } }, 'Saved')}>
          <option value="complete_with_errors">Count it and carry on</option>
          <option value="fail_run">Stop the run</option>
        </Select>
        <p className="text-[11px] text-[var(--text-ter)] mt-1">Either way it never makes something up to fill the gap.</p>
      </div>
    </SwitchGroup>
  );
}
